import React, { FC, useEffect, useRef } from "react";
import { Modal, notification } from "antd";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import { Loader } from "shared/ui";
import { useUpdateBatchMutation, useGetBatchByIdQuery } from "shared/api";
import { handleRequestError } from "shared/helpers";
import { Colors } from "shared/themes";
import { BatchFormValues } from "../types";
import { BatchForm } from "./BatchForm";
import { extractOrderIds, extractPackagingIds } from "../utils/batchHelpers";

interface EditBatchModalProps {
  isOpenModal: boolean;
  hideModal: () => void;
  batchId: number | null;
}

const EditBatchModal: FC<EditBatchModalProps> = ({
  isOpenModal,
  hideModal,
  batchId,
}) => {
  const { t } = useTranslation();
  const [api, contextHolder] = notification.useNotification();
  const [updateBatch, { isLoading }] = useUpdateBatchMutation();

  const {
    data: batch,
    isLoading: isLoadingBatch,
    isFetching: isFetchingBatch,
  } = useGetBatchByIdQuery(
    {
      id: batchId!,
      expand:
        "orders.order,company,packagings.packaging.order,packagings.packaging.material",
    },
    {
      skip: !batchId,
    }
  );

  const {
    values,
    resetForm,
    setFieldValue,
    validateForm,
    isValid,
    setTouched,
  } = useFormikContext<BatchFormValues>();

  const hasLoadedBatchRef = useRef(false);

  useEffect(() => {
    if (!isOpenModal) {
      hasLoadedBatchRef.current = false;
      resetForm();
    }
  }, [isOpenModal, resetForm]);

  useEffect(() => {
    const shouldLoadBatch = batch && isOpenModal && !hasLoadedBatchRef.current;

    if (shouldLoadBatch) {
      hasLoadedBatchRef.current = true;

      setFieldValue("name", batch.name, false);
      setFieldValue("description", batch.description, false);

      const orderIds = extractOrderIds(batch.orders);
      const packagingIds = extractPackagingIds(batch.packagings);

      setFieldValue("orders", orderIds, false);
      setFieldValue("packagings", packagingIds, false);

      const companyId =
        typeof batch.company === "object" ? batch.company.id : batch.company;
      setFieldValue("company", companyId ?? null, false);

      setTouched({});

      setTimeout(() => {
        validateForm();
      }, 0);
    }
  }, [batch, isOpenModal, setFieldValue, validateForm, setTouched]);

  const handleSubmit = async () => {
    if (!batchId || !isValid) return;

    try {
      await updateBatch({ id: batchId, ...values }).unwrap();

      api.success({
        message: t("success"),
        description: "Batch successfully updated",
      });

      hideModal();
    } catch (error) {
      api.error({
        message: t("error"),
        description: handleRequestError(error, "Failed to update batch"),
      });
    }
  };

  if (isLoadingBatch || isFetchingBatch) {
    return <Loader type="fullscreen-backdrop" />;
  }

  return (
    <>
      {contextHolder}
      <Modal
        title="Edit Batch"
        open={isOpenModal}
        onOk={handleSubmit}
        onCancel={hideModal}
        confirmLoading={isLoading}
        width={600}
        okButtonProps={{
          disabled: !isValid,
          style: {
            backgroundColor: Colors.blue.primary,
            color: "#fff",
            borderColor: Colors.blue.primary,
          },
        }}
      >
        <BatchForm
          clearPackagingsOnOrderChange={false}
          isBatchLoaded={!!batch && !isLoadingBatch && !isFetchingBatch}
          existingPackagings={batch?.packagings}
        />
      </Modal>
    </>
  );
};

export { EditBatchModal };
